<template>
  <div>
    <div v-if="project.projectInfo.checkingAccount" class="w-full flex flex-col gap-2 m-auto h-[700px]">
      <Card class="px-1 h-fit w-full my-auto" pt:content:class="p-0!">
        <template #content>
          <div class="flex justify-evenly items-center gap-2">
            <div>
              <span class="text-sm">Loan To Pay</span>
              <h1 class="font-bold text-5xl mb-3">{{ formatCurrency(availableLoan) }}</h1>
            </div>
            <div>
              <span class="text-sm">Equity To Pay</span>
              <h1 class="font-bold text-5xl mb-3">{{ formatCurrency(availableEquity) }}</h1>
            </div>
            <div>
              <span class="text-sm">Ledger</span>
              <h1 class="font-bold text-5xl mb-3">{{ project.projectInfo.checkingAccount.description }}</h1>
            </div>
            <div>
              <span class="text-sm">Default Payee</span>
              <h1 class="font-bold text-5xl mb-3">{{ defaultPayee }}</h1>
            </div>
            <div class="flex flex-col gap-2">
              <Button outlined label="Pay To Default Payee" icon="pi pi-user" class="h-10" @click="payDefault = true"></Button>
              <Button outlined label="Split Payment" icon="pi pi-percentage" class="h-10" @click="paySplit = true"></Button>
              <div class="flex flex-row my-auto">
                <Checkbox v-model="project.projectSettings.enableEquityPayments" :binary="true" @change="changeEquityPayments"></Checkbox>
                <p class="my-auto mx-2">Enable Equity Payments</p>
              </div>
            </div>
          </div>
        </template>
      </Card>
      <PaymentTable
        ref="paymentTable"
        v-model:ledger="project.projectInfo.checkingAccount!"
        :project="project"
        :permissions="permissions"
      ></PaymentTable>
    </div>
    <TSDialog v-model:visible="payDefault" :label="'Pay ' + defaultPayee" icon="user" v-on:after-hide="payDefault = false">
      <template #content>
        <PayDefaultPayee
          :project="project"
          :available-loan="availableLoan"
          :available-equity="availableEquity"
          @paid="
            payDefault = false;
            reloadAmounts();
            paymentTable.reloadPayments();
          "
          @cancelled="payDefault = false"
        />
      </template>
    </TSDialog>
    <TSDialog v-model:visible="paySplit" label="Split Payment" icon="percentage" size=" h-full w-[1100px]!" v-on:after-hide="paySplit = false">
      <template #content>
        <SplitPayment
          :project="project"
          :project-entities="entities"
          :available-loan="availableLoan"
          :available-equity="availableEquity"
          @cancelled="paySplit = false"
          @paid="
            paySplit = false;
            reloadAmounts();
            paymentTable.reloadPayments();
          "
        ></SplitPayment>
      </template>
    </TSDialog>
  </div>
</template>

<script setup lang="ts">
import { usePropsStore } from "@/stores/props";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { ProjectEntityListSchema, type ProjectEntity } from "@/types/ProjectEntity";
import type { Project } from "@/types/Projects";
import { buildUrl, formatCurrency, getEntityDisplay, handleApiResponse } from "@/services/Utility";
import type { UserPermissions } from "@/types/User";

const project = defineModel<Project>("project", { required: true });

interface Props {
  permissions: UserPermissions;
}

const props = defineProps<Props>();

const route = useRoute();

const paymentTable = ref();

const availableLoan = ref<number>(0);
const availableEquity = ref<number>(0);
const entities = ref<ProjectEntity[]>([]);

const payDefault = ref(false);
const paySplit = ref(false);

const propsStore = usePropsStore();

const ledgerParams = computed<Map<string, string | number>>(() => {
  return new Map<string, string | number>([
    ["projectId", project.value.projectInfo.projectId],
    ["drawNumber", project.value.projectDrawInfo.drawNumber],
  ]);
});

const defaultPayee = computed<string>(() => {
  if (entities.value.length == 0) return "";
  for (let entity of entities.value) {
    if (entity.defaultPayee) {
      return getEntityDisplay(entity.entity);
    }
  }
  return getEntityDisplay(project.value.projectInfo.builder);
});

const changeEquityPayments = () => {
  axios
    .post(
      buildUrl(
        `/ledger/${project.value.projectInfo.checkingAccount?.checkingAccountId}/enableEquityPayments`,
        new Map<string, any>([
          ["projectId", project.value.projectInfo.projectId],
          ["drawNumber", project.value.projectDrawInfo.drawNumber],
          ["enableEquityPayments", project.value.projectSettings.enableEquityPayments],
        ])
      )
    )
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          availableLoan.value = data.loanAmount;
          availableEquity.value = data.equityAmount;
        },
        (errors: ApiError[]) => {
          error("Error Occurred", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

onBeforeMount(async (): Promise<void> => {
  reloadDraw();
});

const reloadDraw = (): void => {
  reloadAmounts();

  axios
    .get(`/project-entity/${route.params.id}/${project.value.projectDrawInfo.drawNumber}`)
    .then((response) => (entities.value = ProjectEntityListSchema.parse(response.data.result)))
    .catch((e) => errorApi(e));

  if (paymentTable.value) paymentTable.value.reloadPayments();
};

const reloadAmounts = async (): Promise<void> => {
  await axios
    .get(buildUrl(`/ledger/${project.value.projectInfo.checkingAccount?.checkingAccountId}/amounts`, ledgerParams.value))
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          availableLoan.value = data.loanAmount;
          availableEquity.value = data.equityAmount;
        },
        (errors: ApiError[]) => {
          error("Error Occurred", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

defineExpose({ reloadDraw });
</script>
